/**
 * Content Package API service.
 * Provides CRUD operations for content packages.
 *
 * API Endpoints:
 * - GET /contentpackages - List all content packages
 * - GET /contentpackages/:id - Get content package by ID
 * - POST /contentpackages/upload - Create content package by uploading binary content
 * - DELETE /contentpackages/:id - Delete content package
 *
 * @module services/contentPackageService
 */

import { httpClient } from './httpClient';
import type { ContentPackageResponse, ContentPackageListResponse } from '@/types';

const ENDPOINT = '/contentpackages';

/** Build the scope-appropriate base path for content package routes. */
function basePath(sandboxGroupId?: string): string {
  return sandboxGroupId
    ? `/sandboxGroups/${encodeURIComponent(sandboxGroupId)}/contentpackages`
    : ENDPOINT;
}

/**
 * Content Package Service
 * Encapsulates all content package API operations.
 * All methods accept an optional sandboxGroupId; when provided the request is
 * routed against the group-scoped endpoint (parity with diskImageService).
 */
export const contentPackageService = {
  /**
   * List all content packages
   * @param labels - Optional label filter (e.g., { name: 'foo' })
   * @param sandboxGroupId - Optional sandbox group ID for group-scoped content packages
   * @returns Promise<ContentPackageResponse[]> - Array of content packages
   */
  async list(
    labels?: Record<string, string>,
    sandboxGroupId?: string
  ): Promise<ContentPackageResponse[]> {
    console.log(
      '[ContentPackageService] Fetching all content packages...',
      sandboxGroupId ? `(group: ${sandboxGroupId})` : ''
    );
    try {
      const params: Record<string, string> = {};
      if (labels && Object.keys(labels).length > 0) {
        params.labels = Object.entries(labels)
          .map(([k, v]) => `${k}=${v}`)
          .join(',');
      }
      const response = await httpClient.get<ContentPackageListResponse>(basePath(sandboxGroupId), {
        params,
      });
      console.log(
        '[ContentPackageService] Fetched',
        response.data.value.length,
        'content packages'
      );
      return response.data.value;
    } catch (error) {
      console.error('[ContentPackageService] Failed to fetch content packages:', error);
      throw error;
    }
  },

  /**
   * Get a content package by ID
   * @param id - The ID of the content package
   * @param sandboxGroupId - Optional sandbox group ID for group-scoped content packages
   * @returns Promise<ContentPackageResponse> - The content package
   */
  async getById(id: string, sandboxGroupId?: string): Promise<ContentPackageResponse> {
    console.log(
      '[ContentPackageService] Fetching content package:',
      id,
      sandboxGroupId ? `(group: ${sandboxGroupId})` : ''
    );
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.get<ContentPackageResponse>(
        `${basePath(sandboxGroupId)}/${encodedId}`
      );
      console.log('[ContentPackageService] Fetched content package:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[ContentPackageService] Failed to fetch content package:', id, error);
      throw error;
    }
  },

  /**
   * Create a content package by uploading binary content
   * @param file - The file to upload
   * @param labels - Optional labels for the content package
   * @param sandboxGroupId - Optional sandbox group ID for group-scoped content packages
   * @returns Promise<ContentPackageResponse> - The created content package
   */
  async create(
    file: File,
    labels?: Record<string, string>,
    sandboxGroupId?: string
  ): Promise<ContentPackageResponse> {
    console.log(
      '[ContentPackageService] Creating content package:',
      file.name,
      sandboxGroupId ? `(group: ${sandboxGroupId})` : ''
    );
    try {
      const params: Record<string, string> = {};
      if (labels && Object.keys(labels).length > 0) {
        params.labels = Object.entries(labels)
          .map(([k, v]) => `${k}=${v}`)
          .join(',');
      }

      const response = await httpClient.post<ContentPackageResponse>(
        `${basePath(sandboxGroupId)}/upload`,
        file,
        {
          params,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
        }
      );
      console.log('[ContentPackageService] Created content package:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[ContentPackageService] Failed to create content package:', error);
      throw error;
    }
  },

  /**
   * Delete a content package by ID
   * @param id - The ID of the content package to delete
   * @param sandboxGroupId - Optional sandbox group ID for group-scoped content packages
   * @returns Promise<void>
   */
  async delete(id: string, sandboxGroupId?: string): Promise<void> {
    console.log(
      '[ContentPackageService] Deleting content package:',
      id,
      sandboxGroupId ? `(group: ${sandboxGroupId})` : ''
    );
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.delete(`${basePath(sandboxGroupId)}/${encodedId}`);
      console.log('[ContentPackageService] Deleted content package:', id);
    } catch (error) {
      console.error('[ContentPackageService] Failed to delete content package:', id, error);
      throw error;
    }
  },
};
